export type CountryOption = {
  code: string;
  name: string;
  dialCode: string; // e.g. "+1"
  mask: string; // e.g. "(###) ###-####"
  maxLength: number; // number of digits (without dial code)
};

export const COUNTRY_OPTIONS: CountryOption[] = [
  {
    code: 'AU',
    name: 'Australia',
    dialCode: '+61',
    mask: '#### ### ###',
    maxLength: 10, // Australian numbers are 9-10 digits
  },
  {
    code: 'US',
    name: 'United States',
    dialCode: '+1',
    mask: '(###) ###-####',
    maxLength: 10,
  },
  {
    code: 'CA',
    name: 'Canada',
    dialCode: '+1',
    mask: '(###) ###-####',
    maxLength: 10,
  },
];

const FALLBACK_COUNTRY_CODE = 'AU';

export const DEFAULT_COUNTRY_CODE =
  process.env.NEXT_PUBLIC_DEFAULT_COUNTRY_CODE ||
  process.env.DEFAULT_COUNTRY_CODE ||
  FALLBACK_COUNTRY_CODE;

export const getCountryByCode = (code?: string): CountryOption => {
  const targetCode = code
    ? code.toLowerCase()
    : DEFAULT_COUNTRY_CODE.toLowerCase();
  const found = COUNTRY_OPTIONS.find(
    (country) => country.code.toLowerCase() === targetCode
  );
  return (
    found ||
    COUNTRY_OPTIONS.find(
      (country) =>
        country.code.toLowerCase() === FALLBACK_COUNTRY_CODE.toLowerCase()
    ) ||
    COUNTRY_OPTIONS[0]
  );
};

export const getCountryByDialDigits = (dialDigits: string): CountryOption => {
  const found = COUNTRY_OPTIONS.find((country) =>
    dialDigits.startsWith(country.dialCode.replace(/[^0-9]/g, ''))
  );
  return found ?? getCountryByCode(DEFAULT_COUNTRY_CODE);
};

export const applyMask = (digits: string, mask: string): string => {
  // No formatting - just return digits
  return digits;
};

export const toE164 = (country: CountryOption, digits: string): string => {
  const sanitizedDigits = digits.replace(/\D/g, '').slice(0, country.maxLength);
  if (!sanitizedDigits) return '';
  const dialDigits = country.dialCode.replace(/[^0-9]/g, '');
  return `+${dialDigits}${sanitizedDigits}`;
};

export const extractDigits = (value: string): string =>
  value.replace(/\D/g, '');

export const parsePhoneNumber = (
  value: string | undefined | null,
  countryCode?: string
): { country: CountryOption; digits: string } => {
  if (!value) {
    const country = getCountryByCode(countryCode);
    return { country, digits: '' };
  }

  const digitsOnly = extractDigits(value);

  let country = countryCode
    ? getCountryByCode(countryCode)
    : getCountryByDialDigits(digitsOnly);

  // Remove dial prefix if present
  const dialDigits = country.dialCode.replace(/[^0-9]/g, '');
  let localDigits = digitsOnly;
  if (localDigits.startsWith(dialDigits)) {
    localDigits = localDigits.slice(dialDigits.length);
  }

  // If nothing remains but we still have digits, fall back to original digits (legacy data)
  if (!localDigits && digitsOnly.length <= country.maxLength) {
    localDigits = digitsOnly.slice(0, country.maxLength);
  }

  if (!localDigits) {
    return { country, digits: '' };
  }

  return {
    country,
    digits: localDigits.slice(0, country.maxLength),
  };
};

export const formatPhoneForDisplay = (
  digits: string,
  country: CountryOption
): string => {
  return digits;
};

export const ensureE164 = (phone: string, countryCode?: string): string => {
  if (!phone) return '';
  if (phone.startsWith('+')) return phone;
  const { country } = parsePhoneNumber(phone, countryCode);
  return toE164(country, extractDigits(phone));
};

export const isSamePhone = (
  valueA: string | undefined | null,
  valueB: string | undefined | null
): boolean => {
  if (!valueA || !valueB) return false;
  const digitsA = extractDigits(valueA);
  const digitsB = extractDigits(valueB);
  if (!digitsA || !digitsB) return false;
  return (
    digitsA === digitsB ||
    digitsA.endsWith(digitsB) ||
    digitsB.endsWith(digitsA)
  );
};
